import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon, CopyIcon } from '@/ui/icons';
import './RightSidebar.css';

// Reusable right-side drawer for the whole app. Slides in over the page,
// resizable via the left-edge handle (mouse, touch, or keyboard), and closes
// on the esc chip, a click outside the panel, or the Escape shortcut. While open it traps
// focus, locks page scroll, and restores focus to the trigger on close. The
// chosen width persists across sessions in localStorage.
const MIN_WIDTH = 320;
const DEFAULT_WIDTH = 384;
const RESIZE_STEP = 24;
// Keep in sync with the longest transition in RightSidebar.css so the exit
// animation finishes before the node unmounts.
const ANIM_MS = 240;
const STORAGE_KEY = 'rsb:width';
// Fallback nav width (matches the expanded left sidebar, .sidebar-b = 216px).
const NAV_FALLBACK = 216;

// The drawer must never cover the app's left nav: its MAX width is the content
// area = viewport minus the nav's right edge. So "maximize" reaches the nav and
// dragging stops there. Read live so it tracks the nav's collapsed/expanded
// state and window size.
function maxDrawerWidth() {
  if (typeof window === 'undefined') return 760;
  const nav = document.querySelector('.sidebar-b');
  const navRight = nav
    ? Math.round(nav.getBoundingClientRect().right)
    : NAV_FALLBACK;
  return Math.max(MIN_WIDTH, window.innerWidth - navRight);
}

const clampWidth = (w) => Math.min(maxDrawerWidth(), Math.max(MIN_WIDTH, w));

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function readStoredWidth(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    const n = raw == null ? NaN : Number(raw);
    return Number.isFinite(n) ? clampWidth(n) : fallback;
  } catch {
    return fallback;
  }
}

export default function RightSidebar({
  open,
  onClose,
  title,
  subtitle,
  titleVariant = 'code',
  subtitleMono = false,
  kicker,
  code,
  copyText,
  children,
  footer,
  defaultWidth = DEFAULT_WIDTH,
  storageKey = STORAGE_KEY,
  // Forms opt out of click-outside dismissal so a stray click can't discard
  // unsaved input — they close only via Cancel, Esc, or a successful submit.
  dismissOnOutsideClick = true,
}) {
  // `render` keeps the node mounted through the exit transition; `entered`
  // drives the open class that runs the slide/fade.
  const [render, setRender] = useState(open);
  const [entered, setEntered] = useState(false);
  const [width, setWidth] = useState(() =>
    readStoredWidth(storageKey, clampWidth(defaultWidth)),
  );
  const [copied, setCopied] = useState(false);

  const panelRef = useRef(null);
  const draggingRef = useRef(false);
  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  const commitWidth = useCallback(
    (w) => {
      const next = clampWidth(w);
      setWidth(next);
      try {
        window.localStorage.setItem(storageKey, String(next));
      } catch {
        /* storage unavailable — keep the in-memory width */
      }
    },
    [storageKey],
  );

  // Mount on open; on close, play the exit transition, then unmount.
  useEffect(() => {
    if (open) {
      setRender(true);
      return undefined;
    }
    setEntered(false);
    if (prefersReducedMotion()) {
      setRender(false);
      return undefined;
    }
    const t = setTimeout(() => setRender(false), ANIM_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Once mounted, flip to the entered state on the next frame so the
  // transition has a starting point to animate from.
  useEffect(() => {
    if (!render) return undefined;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [render]);

  // Non-modal inspector: the page stays scrollable AND clickable while the
  // drawer is open, so the user can pick another row and swap it instantly.
  // No scroll lock, no overlay, no focus trap. Escape (from anywhere) closes.
  useEffect(() => {
    if (!render) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [render, onClose]);

  // Click-outside-to-close. Because the drawer is non-modal there's no overlay
  // to catch the click, so we listen on the document and dismiss when the press
  // lands outside the panel. Clicks that drive the inspector are exempt so the
  // list keeps working: a `.ledger` row (the swap mechanism and its sort heads),
  // a `.modal-backdrop` (a dialog layered above must not dismiss the drawer
  // behind it), and any `[data-rsb-keep-open]` opt-out region. Anything else —
  // empty page, toolbar, nav — closes and unmounts the drawer (no residue: the
  // node returns null once the exit transition ends and `useRetained` only ever
  // re-renders the next selection).
  useEffect(() => {
    if (!render || !dismissOnOutsideClick) return undefined;
    const onPointerDown = (e) => {
      const target = e.target;
      if (
        panelRef.current?.contains(target) ||
        target?.closest?.('.ledger, .modal-backdrop, [data-rsb-keep-open]')
      ) {
        return;
      }
      onClose?.();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [render, onClose, dismissOnOutsideClick]);

  // Drag-to-resize from the left edge — pointer events cover mouse and touch.
  const onResizePointerDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }, []);

  const onResizePointerMove = useCallback((e) => {
    if (!draggingRef.current) return;
    // clampWidth caps the drawer at the nav's right edge, so dragging left
    // stops when the drawer meets the left sidebar — it never pushes into it
    // or collapses it.
    setWidth(clampWidth(window.innerWidth - e.clientX));
  }, []);

  // "Maximize" (double-click handle / Home key): snap to the widest the drawer
  // can be without covering the left nav — its right edge. Never collapses it.
  const maximize = useCallback(() => {
    commitWidth(maxDrawerWidth());
  }, [commitWidth]);

  const endResize = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      commitWidth(widthRef.current);
    },
    [commitWidth],
  );

  // Keyboard resize for the separator role.
  const onResizeKeyDown = useCallback(
    (e) => {
      if (e.key === 'Home') {
        e.preventDefault();
        maximize();
        return;
      }
      let next = null;
      if (e.key === 'ArrowLeft') next = widthRef.current + RESIZE_STEP;
      else if (e.key === 'ArrowRight') next = widthRef.current - RESIZE_STEP;
      else if (e.key === 'End') next = MIN_WIDTH;
      if (next == null) return;
      e.preventDefault();
      commitWidth(next);
    },
    [commitWidth, maximize],
  );

  if (!render) return null;

  const isMax = width >= maxDrawerWidth() - 1;

  return (
    <>
      <aside
        ref={panelRef}
        className={`rsb${entered ? ' rsb--open' : ''}`}
        // Clamp at render so the drawer self-corrects if the nav re-expands
        // while it's at max (it would otherwise overlap the restored rail).
        style={{ width: clampWidth(width) }}
        role="complementary"
        aria-label={typeof title === 'string' ? title : 'Panel'}
      >
        <div
          className="rsb__resize"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          onKeyDown={onResizeKeyDown}
          onDoubleClick={() => (isMax ? commitWidth(defaultWidth) : maximize())}
          role="separator"
          aria-orientation="vertical"
          aria-label="Cambiar ancho del panel"
          title="Arrastra para ajustar · doble clic para maximizar o restablecer"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={maxDrawerWidth()}
          aria-valuenow={Math.round(width)}
          tabIndex={0}
        >
          <span className="rsb__resize-grip" aria-hidden="true" />
        </div>
        <header className="rsb__head">
          <div className="rsb__titles">
            {kicker != null && <div className="rsb__kicker">{kicker}</div>}
            {code != null && <div className="rsb__rol">{code}</div>}
            {title != null && (
              <div className={`rsb__title rsb__title--${titleVariant}`}>
                {title}
              </div>
            )}
            {copyText != null && (
              <div className="rsb__code">
                <span className="rsb__code-text">{copyText}</span>
                <button
                  type="button"
                  className="rsb__copy"
                  onClick={() => {
                    try {
                      navigator.clipboard?.writeText(String(copyText));
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1200);
                    } catch {
                      /* clipboard unavailable */
                    }
                  }}
                  title={copied ? 'Copiado' : 'Copiar ID'}
                  aria-label="Copiar ID"
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </button>
              </div>
            )}
            {subtitle != null && (
              <div
                className={`rsb__subtitle${subtitleMono ? ' rsb__subtitle--mono' : ''}`}
              >
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            className="rsb__close"
            onClick={onClose}
            aria-label="Cerrar panel (Esc)"
            title="Cerrar (Esc)"
          >
            esc
          </button>
        </header>
        <div className="rsb__body">{children}</div>
        {footer != null && <footer className="rsb__foot">{footer}</footer>}
      </aside>
    </>
  );
}

RightSidebar.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  titleVariant: PropTypes.oneOf(['code', 'prose']),
  subtitleMono: PropTypes.bool,
  kicker: PropTypes.node,
  code: PropTypes.node,
  copyText: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  defaultWidth: PropTypes.number,
  storageKey: PropTypes.string,
  dismissOnOutsideClick: PropTypes.bool,
};
